import { computed, Injectable } from '@angular/core';
import { AutomationEditorService } from './automation-editor.service';
import { Edge } from '@dagrejs/graphlib';
import {
  ADD_STEP_PREFIX,
  AutomationGraph,
  END_STEP_ID,
  EXPANDED_GROUP_END_PREFIX,
  EXPANDED_GROUP_START_PREFIX,
  GROUP_STEP_PREFIX,
  HIDDEN_STEP_PREFIX,
  PASSTHROUGH_STEP_PREFIX,
  TracedBranchNode,
} from './automation-graph';
import { WorkflowStep } from '@vendasta/automata';

/**
 * A class that helps the automation editor manipulate and interpret its node graph.
 */
@Injectable({ providedIn: 'root' })
export class AutomationGraphUtilitiesService {
  private automationGraph = computed(() => new AutomationGraph([...this.automationEditorService.graphMap().values()]));

  constructor(private automationEditorService: AutomationEditorService) {}

  public getAllNodesThatAreInTheSameBranchLaneAsMe(nodeId: string): Set<string> {
    // This function will be run on the child of a branch. Go up one and get the parent (i.e. the branch node)
    const parents = this.getParentNodes(nodeId);
    if (!parents?.length) {
      return new Set<string>(); // We have reached the top of the graph
    }
    const parent = parents[0];

    return this.getAllNodesThatAreInTheSameBranchLaneAsMeHelper(parent, 1);
  }

  // Return all the nodes in the same branch, including further nested branches
  private getAllNodesThatAreInTheSameBranchLaneAsMeHelper(nodeId: string, distance: number): Set<string> {
    let set = new Set<string>();

    const childrenNodes: string[] = this.getChildrenNodes(nodeId);
    if (childrenNodes?.length > 1 && distance === 0) {
      return set; // We have reach the branch node that we are inside
    }
    const parents = this.getParentNodes(nodeId);
    if (!parents?.length) {
      return set; // We have reached the top of the graph
    }

    if (childrenNodes?.length > 1) {
      distance--;
    }
    if (parents.length > 1) {
      distance++;
    }

    set.add(nodeId);
    parents.forEach((parent) => {
      set = new Set<string>([...set, ...this.getAllNodesThatAreInTheSameBranchLaneAsMeHelper(parent, distance)]);
    });

    return set;
  }

  public getRootBranchForNode(nodeId: string): string {
    if (this.automationGraph().rootBranchingNodes.has(nodeId)) {
      return nodeId;
    }

    const parents = this.getParentNodes(nodeId);
    if (!parents?.length) {
      // Only the trigger has no parents...
      return 'trigger';
    }
    const branchingNode = this.automationGraph().traceNodeUpToFirstBranchingNode(parents[0]);
    if (!branchingNode) {
      return 'trigger';
    }
    if (this.automationGraph().rootBranchingNodes.has(branchingNode.nodeId)) {
      return branchingNode.nodeId;
    }
    const parents2 = this.getParentNodes(branchingNode.nodeId);
    if (!parents2?.length) {
      // Only the trigger has no parents...
      return 'trigger';
    }
    return this.getRootBranchForNode(parents2[0]);
  }

  /**
   * Get all the nodes in the branch starting at n
   * This will include n and all the nodes that are in the branch that n is in
   */
  public getAllNodesInBranch(n: string): Set<string> {
    // Get the children of the node
    const children = this.getChildrenNodes(n);

    // Not actually a branch
    if (children.length < 2) {
      return new Set([n]);
    }

    let result = new Set([n]);
    const branches = children.length - 1;

    for (const child of children) {
      result = new Set([...result, ...this.getAllNodesInBranchHelper(child, branches)]);
    }

    return result;
  }

  // getAllNodesInBranchHelper goes through the graph and returns the nodes that are part of the branch, recursively
  private getAllNodesInBranchHelper(n: string, branches: number): Set<string> {
    // Get the children of the nodes
    const children = this.getChildrenNodes(n);
    const parents = this.getParentNodes(n);

    // We've branched further and need to count up
    if (children.length > 1) {
      branches += children.length - 1;
    }

    // We're converging and need to count down
    if (parents.length > 1) {
      branches -= parents.length - 1;
    }

    if (branches === 0) {
      return new Set();
    }

    let result = new Set([n]);

    for (const child of children) {
      result = new Set([...result, ...this.getAllNodesInBranchHelper(child, branches)]);
    }

    return result;
  }

  // This function will ignore add-step, hidden-step, and terminating steps.
  public getImmediateParents(nodeId: string): string[] {
    const parents = this.getParentNodes(nodeId);
    if (!parents?.length) {
      return [];
    }
    let result = new Set<string>(); // Use a Set to dedupe
    for (const parent of parents) {
      const followedParent = this.getImmediateParentsHelper(parent);
      followedParent.forEach((p) => {
        // If the parent is a group we need to get the actual parent nodes from groupData
        const group = this.automationGraph().groupData.get(p);
        if (group) {
          result = new Set([...result, ...group.bottomNodes]);
        } else {
          result.add(p);
        }
      });
    }
    return Array.from(result);
  }

  private getImmediateParentsHelper(nodeId: string): string[] {
    if (this.automationGraph().terminatingStepIds.includes(nodeId) || nodeId.startsWith(HIDDEN_STEP_PREFIX)) {
      // Terminating steps are not considered parents
      return [];
    }
    if (
      !nodeId.startsWith(ADD_STEP_PREFIX) &&
      !nodeId.startsWith(PASSTHROUGH_STEP_PREFIX) &&
      !nodeId.startsWith(EXPANDED_GROUP_START_PREFIX) &&
      !nodeId.startsWith(EXPANDED_GROUP_END_PREFIX)
    ) {
      return [nodeId];
    }

    const parents: string[] = this.getParentNodes(nodeId);
    if (parents?.length <= 0) {
      return [];
    }
    const resp = [];
    for (const parent of parents) {
      resp.push(...this.getImmediateParentsHelper(parent));
    }
    return resp;
  }

  // findNextAction will ignore add-step and hidden-step nodes to find the next Action in the graph (or end step)
  public findNextAction(nodeId: string): string {
    const children = this.getChildrenNodes(nodeId);
    if (!children?.length) {
      return END_STEP_ID;
    }

    const child = children[0];
    if (this.isAnActualStep(child)) {
      return child;
    }

    return this.findNextAction(children[0]);
  }

  // findNextActions will ignore add-step and hidden-step nodes to find the next Actions in the graph (or end step).
  // This will follow all branches instead of just the left branch like findNextAction
  public findNextActions(nodeId: string): string[] {
    const children = this.getChildrenNodes(nodeId);
    if (!children?.length) {
      return [END_STEP_ID];
    }

    return children.reduce((prev: string[], child, _i, _arr) => {
      {
        if (this.isAnActualStep(child)) {
          prev.push(child);
          return prev;
        }
        return prev.concat(this.findNextActions(child));
      }
    }, []);
  }

  public findPreviousActions(nodeId: string): string[] {
    const parents = this.getParentNodes(nodeId);
    return parents.reduce((prev: string[], parent, _i, _arr) => {
      {
        if (this.isAnActualStep(parent)) {
          prev.push(parent);
          return prev;
        }
        return prev.concat(this.findPreviousActions(parent));
      }
    }, []);
  }

  // isAMergeStep is called for action nodes. We know if it's a merge step if the "Add" step above it has multiple parents
  // This function only works after the whole graph has been built
  public isAMergeStep(nodeId: string): boolean {
    let parents = this.getParentNodes(nodeId);
    if (!parents?.length) {
      // In the event of the node being inside a collapsed group, it won't have any parents, check if that's the case
      for (const groupID of this.automationGraph().groupData.keys()) {
        const group = this.automationGraph().groupData.get(groupID);
        if (group?.topNode === nodeId) {
          parents = this.getParentNodes(groupID);
          break;
        }
      }
    }
    if (!parents?.length) {
      return false;
    }
    if (parents.length > 1) {
      return true;
    }

    let addStep = parents[0];
    let addStepParents = this.getParentNodes(addStep);

    if (addStepParents?.length === 1 && addStepParents[0].startsWith(EXPANDED_GROUP_START_PREFIX)) {
      // In the event of an expanded group we need to consider one level higher
      addStep = this.getParentNodes(addStepParents[0])[0];
      addStepParents = this.getParentNodes(addStep);
    }

    return addStepParents?.length > 1;
  }

  // getMergeStep follows the children until it hits a step with multiple parents
  // It only returns action nodes
  // This function only works after the whole graph has been built
  public getMergeStep(nodeId: string): string | undefined {
    const addStep = this.automationGraph().getMergeStepForBranch(nodeId, false);
    if (addStep === END_STEP_ID) {
      return '';
    }

    let nextAction = this.findNextAction(addStep);
    if (nextAction === END_STEP_ID) {
      return '';
    }

    // These merge steps need to be the true action that is next, not a group related step
    if (nextAction.startsWith(GROUP_STEP_PREFIX)) {
      // Actions in a collapsed group are removed from the graph but metaData about them are kept in groupData
      return this.automationGraph().groupData.get(nextAction)?.topNode;
    }

    if (nextAction.startsWith(EXPANDED_GROUP_START_PREFIX)) {
      // Actions in an expanded group are kept in the graph, just one action further in is the merge step
      nextAction = this.findNextAction(nextAction);
      if (nextAction === END_STEP_ID) {
        return '';
      }
    }

    return nextAction;
  }

  // Is the nodeId a root branching node
  public isRootBranchingNode(nodeId: string): boolean {
    return this.automationGraph().rootBranchingNodes.has(nodeId);
  }

  // Get all the edges in the graph
  public getAllEdges(): Edge[] {
    return this.automationGraph().getAllEdges();
  }

  // Get all nodeIds in the graph
  public getAllNodes(): string[] {
    return this.automationGraph().getAllNodes();
  }

  // Get the nodes that this node is pointing to
  public getChildrenNodes(stepId: string): string[] {
    return this.automationGraph().getChildrenNodes(stepId);
  }

  // Get the nodes that this node is being pointed at by
  public getParentNodes(stepId: string): string[] {
    return this.automationGraph().getParentNodes(stepId);
  }

  public isTerminatingStep(stepId: string): boolean {
    return this.automationGraph().terminatingStepIds.includes(stepId);
  }

  private isAnActualStep(stepId: string): boolean {
    return (
      !stepId.startsWith(ADD_STEP_PREFIX) &&
      !stepId.startsWith(HIDDEN_STEP_PREFIX) &&
      !stepId.startsWith(PASSTHROUGH_STEP_PREFIX)
    );
  }

  getAllNodesInGroup(groupId: string): string[] {
    const result = new Set<string>();

    this.getAllNodesInGroupHelper(EXPANDED_GROUP_START_PREFIX + groupId, groupId, result);

    return Array.from(result);
  }

  /**
   * Does a depth first traversal looking for all nodes that lead to EXPANDED_GROUP_END_PREFIX + groupId
   * Assumes a closed subgraph with no cycles, where all paths lead to the end of the group
   * O(n)
   */
  private getAllNodesInGroupHelper(nodeId: string, groupId: string, nodesInGroup: Set<string>) {
    if (nodesInGroup.has(nodeId)) {
      return [];
    }
    nodesInGroup.add(nodeId);

    if (nodeId === EXPANDED_GROUP_END_PREFIX + groupId || this.isTerminatingStep(nodeId)) {
      // We are at the end of the group
      return [];
    }

    const children = this.getChildrenNodes(nodeId);
    if (!children?.length) {
      return [];
    }

    for (const child of children) {
      this.getAllNodesInGroupHelper(child, groupId, nodesInGroup);
    }
  }

  // checks for the branch head if there is group inside any branch originating from it
  public ifContainGroupAsChild(stepID: string): boolean {
    if (this.getChildrenNodes(stepID)?.length > 1) {
      const allChildNodes = this.getAllNodesInBranch(stepID);
      const hasGroupNode = Array?.from(allChildNodes)?.some(
        (node) => node?.startsWith('expanded-group') || node?.startsWith('group'),
      );
      return hasGroupNode;
    }
    return false;
  }

  public traceNodeUpToTheBranchNodeIAmInside(
    nodeId: string,
    initDistance?: number,
    stopIfMergeIsFirst?: boolean,
    parentToFollow?: string,
  ): TracedBranchNode | null {
    return this.automationGraph().traceNodeUpToTheBranchNodeIAmInside(
      nodeId,
      initDistance,
      stopIfMergeIsFirst,
      parentToFollow,
    );
  }

  // getFirstNode will take a workflow and find the starting step
  // Assumes an unordered list of steps
  public static getFirstStep(workflow: WorkflowStep[]): WorkflowStep {
    const nextSteps = new Set<string>(workflow.map((step) => step.choices?.defaultNextStep));

    return workflow.find((step) => !nextSteps.has(step.id)) || ({} as WorkflowStep);
  }

  // getLastStep will take a workflow and find the last step
  // Assumes an unordered list of steps
  public static getLastStep(workflow: WorkflowStep[]): WorkflowStep {
    let start = workflow[0];

    while (start?.choices?.defaultNextStep) {
      start = workflow.find((step) => step.id === start?.choices?.defaultNextStep) || ({} as WorkflowStep);
    }

    return start;
  }
}
